import _ from 'lodash';
import moment from 'moment';
import { Modal } from 'reactstrap';
import {
  IconButton,
  NormalButton,
  DatePickerInput,
  DropdownInput,
  TimePickerInput,
  CloseIcon as closeIcon,
} from '@/shared/ui';
import useDeviceFilterModal from './useDeviceFilterModal';

const validityList = ['All validity', 'Valid', 'Invalid'];

export type DeviceFilterModalProps = {
  header?: string;
  isOpen?: boolean;
  handleClickFilterButton: (data: any) => void;
  defaultEndDate?: any;
  defaultStartDate?: any;
  onClickClose?: () => void;
  startDate?: any;
  endDate?: any;
  isValid?: any;
  eventType?: any;
  isAiEvent?: boolean;
};

/**
 * Advanced filter dialog narrowing the device event list by trigger time, event type and validity. useDeviceFilterModal holds the draft filter and its per-field errors; the From and To date pickers clamp to the study window, with the To picker's lower bound following the chosen From date, the validity dropdown is hidden for AI events, and the footer either resets the filter or applies it.
 *
 * @param props - Dialog props carrying the header text, open flag, close handler, study date bounds and the AI-event flag.
 * @returns The rendered filter dialog with its trigger-time, event-type and validity controls.
 */
const DeviceFilterModal = ({
  header,
  isOpen,
  handleClickFilterButton,
  defaultEndDate,
  defaultStartDate,
  onClickClose,
  startDate,
  endDate,
  isValid,
  eventType,
  isAiEvent,
}: DeviceFilterModalProps) => {
  const {
    state,
    eventTypeList,
    onChangeStartSearchDate,
    onChangeStartSearchTime,
    onChangeStopSearchDate,
    onChangestopSearchTime,
    onChange,
    onClickResetFilterButton,
    onClickFilterButton,
  } = useDeviceFilterModal({
    handleClickFilterButton,
    defaultEndDate,
    defaultStartDate,
    startDate,
    endDate,
    isValid,
    eventType,
  });

  return (
    <Modal className="middle-modal advanced-filter-modal" isOpen={isOpen} centered>
      <div className="custom-modal-header-container">
        <span className="custom-modal-header">{header}</span>
        <IconButton
          id="strips-device-filter-close-btn"
          isFilled
          iconComponent={<img src={closeIcon} alt="Close icon" />}
          onClick={onClickClose}
        />
      </div>
      <div className="custom-modal-body-container">
        <div className="trigger-time-container">
          <div className="main-title">Trigger time</div>
          <div className="flex justify-between">
            <div className="w-50 pr3">
              <div className="sub-title">From</div>
              <div className="flex mt1">
                <DatePickerInput
                  className="w-50 pr2"
                  name="startSearchDate"
                  placeholder="MM/DD/YYYY"
                  value={state.startSearchDate}
                  onChange={onChangeStartSearchDate}
                  belowLimit={
                    defaultStartDate
                      ? moment(moment(defaultStartDate).format('MM/DD/YYYY'))
                          .startOf('day')
                          .valueOf()
                      : undefined
                  }
                  aboveLimit={
                    defaultEndDate
                      ? moment(moment(defaultEndDate).format('MM/DD/YYYY')).endOf('day').valueOf()
                      : undefined
                  }
                  isError={!_.isEmpty(state.errorObject.startSearchDate)}
                  errorMessage={state.errorObject.startSearchDate}
                />
                <TimePickerInput
                  className="w-50 pl2"
                  onChange={onChangeStartSearchTime}
                  value={state.startSearchTime}
                  isError={!_.isEmpty(state.errorObject.startSearchTime)}
                  errorMessage={state.errorObject.startSearchTime}
                  placeholder="HH:mm"
                />
              </div>
            </div>
            <div className="w-50 pl3">
              <div className="sub-title">To</div>
              <div className="flex mt1">
                <DatePickerInput
                  className="w-50 pr2"
                  name="stopSearchDate"
                  placeholder="MM/DD/YYYY"
                  value={state.stopSearchDate}
                  onChange={onChangeStopSearchDate}
                  belowLimit={
                    state.startSearchDate
                      ? moment(moment(state.startSearchDate).format('MM/DD/YYYY'))
                          .startOf('day')
                          .valueOf()
                      : defaultStartDate
                        ? moment(moment(defaultStartDate).format('MM/DD/YYYY'))
                            .startOf('day')
                            .valueOf()
                        : undefined
                  }
                  aboveLimit={
                    defaultEndDate
                      ? moment(moment(defaultEndDate).format('MM/DD/YYYY')).endOf('day').valueOf()
                      : undefined
                  }
                  isError={!_.isEmpty(state.errorObject.stopSearchDate)}
                  errorMessage={state.errorObject.stopSearchDate}
                />
                <TimePickerInput
                  className="w-50 pl2"
                  onChange={onChangestopSearchTime}
                  value={state.stopSearchTime}
                  isError={!_.isEmpty(state.errorObject.stopSearchTime)}
                  errorMessage={state.errorObject.stopSearchTime}
                  placeholder="HH:mm"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex mt-24">
          <div className="w-100">
            <div className="main-title">Type of event</div>
            <DropdownInput
              className="mt-12"
              name="eventType"
              options={eventTypeList.current}
              value={state.eventType}
              onChange={onChange}
            />
          </div>
        </div>
        {!isAiEvent && (
          <div className="flex mt-24">
            <div className="w-100">
              <div className="main-title">Validity</div>
              <DropdownInput
                className="mt-12"
                name="validity"
                options={validityList}
                value={state.validity}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      <div className="custom-modal-footer-container">
        <NormalButton
          id="strips-device-filter-reset-btn"
          className="reset-filter-btn"
          buttonName="Reset filter"
          isText
          onClick={onClickResetFilterButton}
        />
        <NormalButton
          id="strips-device-filter-confirm-btn"
          className="filter-btn"
          buttonName="Filter"
          isFilled
          onClick={onClickFilterButton}
        />
      </div>
    </Modal>
  );
};

export default DeviceFilterModal;
